import { useState, useMemo, useEffect, useRef } from "react";

type Variable = {
  name: string;
  commonName: string;
  url: string;
};

type Source = {
  sourceName: string;
  datasetName: string;
  datasetLink: string;
  variables: Variable[];
  retrievedOn: string;
};

type AboutDataCardProps = {
  onClose: () => void;
  aboutDescription: string;
  sources: Source[];
  metricDefinition: string;
  calculationFormula: string;
};

export default function AboutDataCard({
  onClose,
  aboutDescription,
  sources,
  metricDefinition,
  calculationFormula,
}: AboutDataCardProps) {
  const [openSourceIndices, setOpenSourceIndices] = useState<number[]>(
    sources.length === 1 ? [0] : [],
  );
  // --- Dynamic padding logic for dataset name/url button ---
  const [dynamicPadding, setDynamicPadding] = useState<{ [key: number]: number }>({});
  const contentRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  // Store single-line heights for each index
  const singleLineHeights = useRef<{ [key: number]: number }>({});
  // Store min-heights for buttons
  const [buttonMinHeights, setButtonMinHeights] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Restore ResizeObserver for dynamic padding
  useEffect(() => {
    const resizeObserver = new window.ResizeObserver((entries) => {
      const newPadding: { [key: number]: number } = {};
      const newMinHeights: { [key: number]: number } = {};
      entries.forEach((entry) => {
        const index = parseInt(entry.target.getAttribute('data-index') || '0');
        const contentHeight = entry.contentRect.height;
        // If we haven't measured the single-line height yet, do so now
        if (!singleLineHeights.current[index]) {
          // Create a hidden clone to measure single-line height
          const clone = entry.target.cloneNode(true) as HTMLElement;
          clone.style.visibility = 'hidden';
          clone.style.position = 'absolute';
          clone.style.width = entry.target.clientWidth + 'px';
          clone.style.whiteSpace = 'nowrap';
          clone.style.height = 'auto';
          document.body.appendChild(clone);
          singleLineHeights.current[index] = clone.offsetHeight;
          document.body.removeChild(clone);
        }
        const basePadding = 4; // px
        const singleLineHeight = singleLineHeights.current[index] || 24; // fallback to 24px
        const desiredBottomSpace = 8; // px - consistent space below dataset name
        if (contentHeight > singleLineHeight) {
          // Add extra padding for wrapped lines
          newPadding[index] = basePadding + (contentHeight - singleLineHeight);
        } else {
          newPadding[index] = basePadding;
        }
        // Calculate min-height for button: source name + dataset name + desired bottom space
        const sourceNameHeight = 24; // Approximate height for source name
        const minHeight = sourceNameHeight + contentHeight + desiredBottomSpace;
        newMinHeights[index] = minHeight;
      });
      setDynamicPadding((prev) => ({ ...prev, ...newPadding }));
      setButtonMinHeights((prev) => ({ ...prev, ...newMinHeights }));
    });
    Object.values(contentRefs.current).forEach((ref) => {
      if (ref) resizeObserver.observe(ref);
    });
    return () => resizeObserver.disconnect();
  }, [sources.length]);

  const { formula, footnotes } = useMemo(() => {
    const SUPERSCRIPT_MAP: { [key: string]: number } = {
      '¹': 1, '²': 2, '³': 3, '⁴': 4, '⁵': 5, 
      '⁶': 6, '⁷': 7, '⁸': 8, '⁹': 9, '⁰': 0
    };
    
    const mainFormula = calculationFormula.split('\\n')[0];
    const footnoteChars = [...new Set(mainFormula.match(/[¹²³⁴⁵⁶⁷⁸⁹⁰]/g) || [])];
    const footnoteNumbers = footnoteChars
      .map(char => SUPERSCRIPT_MAP[char])
      .sort((a, b) => a - b);

    const allVariables = sources.flatMap(s => s.variables);

    const footnoteDetails = footnoteNumbers.map(num => {
      const variable = allVariables[num - 1];
      return variable ? { num, ...variable } : null;
    }).filter(Boolean);

    return { formula: mainFormula, footnotes: footnoteDetails };
  }, [calculationFormula, sources]);

  const renderFormulaWithSuperscripts = (text: string) => {
    const parts = text.split(/([¹²³⁴⁵⁶⁷⁸⁹⁰]+)/);
    return parts.map((part, index) => {
      if (/[¹²³⁴⁵⁶⁷⁸⁹⁰]/.test(part)) {
        return <sup key={index}>{part}</sup>;
      }
      return part;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="w-1/3 md:w-2/3 bg-black bg-opacity-30"
        onClick={onClose}
        aria-label="Close"
      />
      <aside className="relative w-2/3 md:w-1/3 bg-white h-full overflow-y-auto p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-10 right-6 w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-800"
          aria-label="Close"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '40px' }}>
            close
          </span>
        </button>

        <div className="mt-16 space-y-8">
          <div>
            <h2 className="text-2xl mb-5">About the Data</h2>
            <p className="text-neutral-700 opacity-70 mb-15">{aboutDescription}</p>
          </div>

          <div className="mb-10">
            <h3 className="text-2xl mb-5">Sources ({sources.length})</h3>
            <div className="space-y-4">
              {sources.map((source, idx) => {
                const isOpen = openSourceIndices.includes(idx);
                return (
                  <div key={idx} className="border-t border-[#212121] border-opacity-30">
                    <button
                      className="flex justify-between items-start w-full pt-4 text-left"
                      style={{ 
                        paddingBottom: `${dynamicPadding[idx] || 4}px`,
                        minHeight: `${buttonMinHeights[idx] || 60}px`
                      }}
                      onClick={() =>
                        setOpenSourceIndices((prev) =>
                          prev.includes(idx)
                            ? prev.filter((i) => i !== idx)
                            : [...prev, idx],
                        )
                      }
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium mt-2">{source.sourceName}</p>
                        <div
                          ref={el => { contentRefs.current[idx] = el; }}
                          data-index={idx}
                          className="relative min-h-6 min-w-0"
                        >
                          <div className={`absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
                            <div className="flex items-center gap-1">
                              <a 
                                href={source.datasetLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm opacity-70 text-[#212121] underline hover:text-[#CA0079] break-words"
                              >
                                {source.datasetName}
                                <span className="material-symbols-outlined text-sm opacity-70 text-[#212121] flex-shrink-0">
                                arrow_outward
                                </span>
                              </a>
                            </div>
                          </div>
                          <div className={`absolute inset-0 transition-all duration-300 ${!isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
                            <p className="text-sm opacity-70 text-[#212121] break-words">{source.datasetName}</p>
                          </div>
                        </div>
                      </div>
                      <span
                        className={`material-symbols-outlined text-base leading-none flex-shrink-0 transform transition-transform mt-1 ${
                          isOpen ? "-rotate-90" : "rotate-90"
                        }`}
                      >
                        arrow_forward_ios
                      </span>
                    </button>

                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="mb-2">
                        <p className="text-md mt-2">Source Variables: </p>
                        <div className="text-sm">
                          {source.variables.map((v, i) => (
                            <span key={i}>
                              <a
                                href={v.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#212121] opacity-70 underline hover:text-[#CA0079]"
                              >
                                {v.name}
                              </a>
                              {i < source.variables.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                        <div>
                        <p className="text-md opacity-100 text-[#212121] mt-4">Accessed on: </p>
                        <p className="text-sm opacity-70 text-[#212121] mb-1">{source.retrievedOn}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-2xl mb-5">About this Metric</h3>
            <div>
              <h4 className="text-md">Definition</h4>
              <p className="text-neutral-700 text-[#212121] opacity-70 mb-5">{metricDefinition}</p>
            </div>
            <div>
              <h4 className="text-md mb-2">Calculation Formula</h4>
              <div className="bg-gray-50 border border-gray-200 text-[#212121] opacity-70 rounded-md p-4">
                <code className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {renderFormulaWithSuperscripts(formula)}
                </code>
                {footnotes.length > 0 && (
                  <>
                    <hr className="my-4 border-gray-200" />
                    <ol className="list-none text-xs space-y-2">
                      {footnotes.map((note) =>
                        note ? (
                          <li key={note.num}>
                            <sup>{note.num}</sup>{' Source Variable: '}
                            <a
                              href={note.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 underline hover:text-[#CA0079]"
                            >
                              {note.name}
                            </a>{' '}
                          </li>
                        ) : null,
                      )}
                    </ol>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
} 